<template>
  <div class="app-container">
    <!-- 头部信息 -->
    <el-card class="box-card" style="margin-bottom: 20px">
      <div slot="header" class="clearfix">
        <span>{{ $t('CenterForSorting.deviceInfo') }}</span>
        <div style="float: right">
          <el-tag style="margin-right: 10px" type="success">{{ $t('CenterForSorting.open') }}: {{ openChuteCount }}
          </el-tag>
          <el-tag style="margin-right: 10px" type="info">{{ $t('CenterForSorting.close') }}:
            {{ machineInfo.closedChuteCount }}
          </el-tag>
          <el-tag style="margin-right: 10px" type="warning">{{ $t('CenterForSorting.fullBag') }}:
            {{ machineInfo.fullBagCount }}
          </el-tag>
          <el-button type="text" @click="handleGoBack">{{ $t('CenterForSorting.backToList') }}
          </el-button>
        </div>
      </div>
      <el-descriptions
        :column="3" border direction="vertical"
        size="small"
      >
        <el-descriptions-item :label="$t('CenterForSorting.machineName')">{{ machineInfo.machineName }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('CenterForSorting.machineCode')">{{ machineInfo.machineCode }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('CenterForSorting.totalChutes')">{{ machineInfo.chuteCount }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('CenterForSorting.scanRegex')">
          <span style="white-space: pre-wrap">{{ machineInfo.scanNumberRegex }}</span>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 工具栏 -->
    <div
      class="filter-container sticky-toolbar"
      style="
        margin-bottom: 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      "
    >
      <div>
        <el-button
          icon="el-icon-refresh" size="small" type="primary"
          @click="fetchData"
        >{{ $t('CenterForSorting.refresh') }}
        </el-button>
        <el-select
          v-model="autoRefresh"
          :placeholder="$t('CenterForSorting.autoRefresh')"
          size="small"
          style="width: 120px; margin: 0 10px"
          @change="handleAutoRefresh"
        >
          <el-option :label="$t('CenterForSorting.closeAutoRefresh')" :value="0" />
          <el-option :label="$t('CenterForSorting.refresh30s')" :value="30" />
          <el-option :label="$t('CenterForSorting.refresh60s')" :value="60" />
        </el-select>
        <!--导出格口数据-->
        <el-button size="small" type="primary" @click="allExportOrder">{{ $t('tablePrice.exportCompartmentData') }}
        </el-button>
      </div>
      <div class="legend">
        <span class="legend-item"><span class="color-box status-1" />{{ $t('CenterForSorting.open') }}</span>
        <span class="legend-item"><span class="color-box status-0" />{{ $t('CenterForSorting.close') }}</span>
        <span class="legend-item"><span class="color-box status-2" />{{ $t('CenterForSorting.fullBag') }}</span>
      </div>
    </div>

    <!-- 格口网格 -->
    <div v-loading="loading" class="chute-grid">
      <div
        v-for="chute in chuteList"
        :key="chute.chuteNumber"
        :class="'status-' + chute.chuteStatus"
        class="chute-card"
      >
        <div class="chute-header">
          <span class="chute-number">{{ chute.chuteNumber }}</span>
          <el-tag :type="getStatusType(chute.chuteStatus)" size="mini">{{ getStatusText(chute.chuteStatus) }}
          </el-tag>
        </div>
        <div class="chute-content">
          <div :title="chute.ruleName" class="rule-name">
            <span class="rule-name-text">{{
              chute.ruleName || $t('CenterForSorting.noRule')
            }}</span>
            <i
              v-if="chute.ruleName"
              class="el-icon-tickets"
              style="cursor: pointer; color: #00A551; margin-left: 5px; flex-shrink: 0"
              @click.stop="handleRuleDetail(chute)"
            />
          </div>
          <div :class="{ 'bag-count-highlight': Number(chute.bagCount) > 0 }" class="bag-count">
            {{ $t('CenterForSorting.bagCount') }}: {{ chute.bagCount }}
          </div>
        </div>
        <div v-permit:remove="'btn:sorting:chuteView:operation'" class="chute-actions">
          <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, chute)">
            <span class="el-dropdown-link" @click.stop>
              {{ $t('CenterForSorting.operation') }}<i class="el-icon-arrow-down el-icon--right" />
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :disabled="chute.chuteStatus === 1" command="open">{{ $t('CenterForSorting.open') }}
              </el-dropdown-item>
              <el-dropdown-item :disabled="chute.chuteStatus === 0" command="close">{{ $t('CenterForSorting.close') }}
              </el-dropdown-item>
              <el-dropdown-item :disabled="chute.chuteStatus === 2" command="full">{{ $t('CenterForSorting.setFull') }}
              </el-dropdown-item>
              <el-dropdown-item command="clear" divided>{{ $t('CenterForSorting.clearData') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 规则信息对话框 -->
    <rule-condition-detail
      :condition-configs="conditionConfigs"
      :rule-id="currentRuleId"
      :visible.sync="viewConditionsVisible"
    />
  </div>
</template>

<script>
import {
  clearChuteData,
  exportChuteView,
  getChuteView,
  updateChuteStatus
} from '@/api/sorting/machine';
import { getConditionConfigList } from '@/api/sorting/rule';
import RuleConditionDetail from './components/RuleConditionDetail';
import { goResourceTaskList } from '@/utils/goExportList';

export default {
  name: 'ChuteView',
  components: { RuleConditionDetail },
  data() {
    return {
      machineId: null,
      machineInfo: {
        machineName: '',
        machineCode: '',
        chuteCount: 0,
        closedChuteCount: 0,
        fullBagCount: 0,
        scanNumberRegex: ''
      },
      chuteList: [],
      loading: false,
      autoRefresh: 30,
      timer: null,
      dialogVisible: false,
      currentChute: null,
      viewConditionsVisible: false,
      currentRuleId: null,
      conditionConfigs: []
    };
  },
  computed: {
    openChuteCount() {
      return (
        this.machineInfo.chuteCount -
        this.machineInfo.closedChuteCount -
        this.machineInfo.fullBagCount
      );
    }
  },
  watch: {
    '$route.query.machineId'(newMachineId, oldMachineId) {
      if (String(newMachineId || '') === String(oldMachineId || '')) return;
      this.syncMachineIdFromRoute({ forceReload: true });
    }
  },
  created() {
    this.syncMachineIdFromRoute({ forceReload: true });
    this.fetchConditionConfigs();
  },
  activated() {
    this.syncMachineIdFromRoute();
  },
  deactivated() {
    this.stopTimer();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    isCurrentChuteViewRoute() {
      return (
        this.$route.name === 'ChuteView' ||
        this.$route.path === '/expressDataCenter/sortingManagementNew/chuteView'
      );
    },
    syncMachineIdFromRoute({ forceReload = false } = {}) {
      if (!this.isCurrentChuteViewRoute()) {
        this.stopTimer();
        return;
      }

      const routeMachineId = this.$route.query.machineId;
      if (!routeMachineId) {
        this.$message.error(this.$t('CenterForSorting.noDeviceId'));
        this.$router.push('/expressDataCenter/sortingManagementNew/machineList');
        return;
      }

      const machineChanged = String(routeMachineId) !== String(this.machineId || '');
      if (machineChanged) {
        this.machineId = routeMachineId;
        this.machineInfo = {
          machineName: '',
          machineCode: '',
          chuteCount: 0,
          closedChuteCount: 0,
          fullBagCount: 0,
          scanNumberRegex: ''
        };
        this.chuteList = [];
      }

      if (machineChanged || forceReload || !this.chuteList.length) {
        this.fetchData();
      }
      this.startTimer();
    },
    fetchData() {
      if (!this.machineId) return;
      this.loading = true;
      getChuteView({ machineId: this.machineId })
        .then((response) => {
          this.machineInfo = response.data.machineInfo;
          this.chuteList = response.data.chuteList;
        })
        .catch(() => {
          this.stopTimer(); // 出错时停止定时器以防止垃圾请求
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleGoBack() {
      this.$router.go(-1);
    },
    startTimer() {
      this.stopTimer();
      if (this.autoRefresh > 0) {
        this.timer = setInterval(this.fetchData, this.autoRefresh * 1000);
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    handleAutoRefresh() {
      this.startTimer();
    },
    getStatusType(status) {
      switch (status) {
        case 1:
          return 'success';
        case 0:
          return 'info';
        case 2:
          return 'warning';
        default:
          return '';
      }
    },
    getStatusText(status) {
      switch (status) {
        case 1:
          return this.$t('CenterForSorting.open');
        case 0:
          return this.$t('CenterForSorting.close');
        case 2:
          return this.$t('CenterForSorting.fullBag');
        default:
          return this.$t('CenterForSorting.unknown');
      }
    },
    handleChuteClick(chute) {
      this.currentChute = chute;
      this.dialogVisible = true;
    },
    async fetchConditionConfigs() {
      const res = await getConditionConfigList();
      this.conditionConfigs = res.data || [];
    },
    handleRuleDetail(chute) {
      if (!chute.ruleId) return;
      this.currentRuleId = chute.ruleId;
      this.viewConditionsVisible = true;
    },
    handleCommand(command, chute) {
      if (command === 'clear') {
        this.$confirm(
          this.$t('CenterForSorting.confirmClearData', { chuteNo: chute.chuteNumber }),
          this.$t('CenterForSorting.tips'),
          {
            type: 'warning',
            confirmButtonText: this.$t('CenterForSorting.sure'),
            cancelButtonText: this.$t('CenterForSorting.cancel')
          }
        ).then(() => {
          clearChuteData({ chuteId: chute.chuteId || chute.id }).then(() => {
            this.$message.success(this.$t('CenterForSorting.clearSuccess'));
            this.fetchData();
          });
        });
      } else {
        let status = 1;
        if (command === 'close') status = 0;
        if (command === 'full') status = 2;

        updateChuteStatus({ chuteId: chute.chuteId || chute.id, status }).then(() => {
          this.$message.success(this.$t('CenterForSorting.statusUpdateSuccess'));
          this.fetchData();
        });
      }
    },
    // 导出
    allExportOrder() {
      exportChuteView(this.machineId).then((res) => {
        if (res.code === 200) {
          goResourceTaskList();
        }
      });
    }
  }
};
</script>

<style scoped>
.sticky-toolbar {
  position: sticky;
  top: -20px;
  z-index: 10;
  background-color: #fff;
  padding: 10px 0;
}

.chute-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
}

.chute-card {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 140px;
}

.chute-card:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.status-1 {
  background-color: #f0f9eb;
  border-color: #67c23a;
}

.status-0 {
  background-color: #f4f4f5;
  border-color: #909399;
}

.status-2 {
  background-color: #ffdbdb;
  border-color: #bf0000;
}

.chute-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.chute-number {
  font-weight: bold;
  font-size: 18px;
}

.chute-content {
  flex-grow: 1;
}

.rule-name {
  font-size: 12px;
  color: #606266;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.rule-name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bag-count {
  font-size: 14px;
  font-weight: bold;
}

.bag-count-highlight {
  color: #ff9900;
}

.chute-actions {
  text-align: right;
  margin-top: 5px;
}

.legend {
  display: flex;
  gap: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.color-box {
  width: 12px;
  height: 12px;
  margin-right: 5px;
  border: 1px solid #ddd;
}

.color-box.status-1 {
  background-color: #f0f9eb;
  border-color: #67c23a;
}

.color-box.status-0 {
  background-color: #f4f4f5;
  border-color: #909399;
}

.color-box.status-2 {
  background-color: #fdf6ec;
  border-color: #e6a23c;
}
</style>
